"use client";

import Skeleton from "react-loading-skeleton";
import { AuctionListItemProps } from "@/types/auction";
import {
  createContext,
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import AuctionBarActionsPanel from "../AuctionBarFilter/ActionPanel";
import AuctionListItemPagination from "../AuctionListItemPagination";
import Input from "@/components/Elements/Input";
import useServerTime from "@/hooks/useServerTime";
import AuctionPagination from "../AuctionPagination";
import GridView from "./Views/GridView";
import ListView from "./Views/ListView";
import useLiveBids from "@/components/WebSocket/LiveBidsSocket";
import "react-loading-skeleton/dist/skeleton.css";

export const AuctionItemListContext = createContext<any>(null);

const AuctionListItems: React.FC<AuctionListItemProps> = forwardRef(
  (
    {
      auctionItems,
      fetchBids = false,
      visibleItemsData = false,
      auctionItemBids: auctionItemBidsInitial = [],
      hidePagination = false,
      hidePaginationTop = false,
      filterProps,
      isFetching = false,
      showFilter = true,
      isLoading = false,
      error = false,
      maxItems = 0,
      readMoreLink = false,
      searchLayout = false,
      showResetFilterOnNotFound = false,
      setItemNoSearchValue: setSearchValue = false,
      itemNoSearchValue: searchValue = "",
    },
    ref
  ) => {
    const auctionItemIds = auctionItems?.map((item: any) => item.itemId);
    const { auctionItemBidsLive, mergeWithInitialBids } = useLiveBids({
      auctionItemIds,
    });
    const [auctionItemsUpdated, setAuctionItemsUpdated] =
      useState(auctionItems);

    const { options, setOptions } = filterProps;
    const paginationArgs = options?.pagination as any;
    const totalItems = auctionItems?.length || 0;
    const [layoutType, setLayoutType] = useState<"grid" | "list">("grid");
    const serverTime = useServerTime();
    const [showScrollTop, setShowScrollTop] = useState(false);

    const [animateDuration, setAnimateDuration] = useState(0);

    const totalPaginationItems = !!paginationArgs.total;

    useEffect(() => {
      const timer = setTimeout(() => {
        setAnimateDuration(1000);
      }, 5000);
      return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
      const handleScroll = () => {
        const scrollY = window.scrollY;
        const pageHeight = document.body.scrollHeight;

        if (scrollY > pageHeight * 0.2) {
          setShowScrollTop(true);
        } else {
          setShowScrollTop(false);
        }
      };

      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    };

    const clearFilter = () => {
      setOptions((prev: any) => ({
        ...prev,
        filtering: {
          ...prev.filtering,
          selections: [],
        },
        pagination: {
          ...prev.pagination,
          page: 1,
        },
        searchItemNo: "",
      }));
    };

    const visibleItemsDataList = auctionItems?.filter((i: any) => !i.isHidden);
    const firstVisibleId = visibleItemsDataList
      ? visibleItemsDataList[0]?.itemId
      : null;

    const updatedAutoBidsInitial = (Array.isArray(auctionItemBidsInitial) ? auctionItemBidsInitial : []).map((bid: any) => {
      const liveBid = auctionItemBidsLive.find(
        (entry: any) => String(entry.itemId) === String(bid.itemId)
      );

      // Prioritize WebSocket data (real-time) over API data (polling)
      const leadingAutoBid = liveBid?.leadingAutoBid !== undefined
        ? liveBid?.leadingAutoBid
        : bid.leadingAutoBid;

      return {
        ...bid,
        leadingAutoBid,
      };
    });

    const auctionItemBids = mergeWithInitialBids(updatedAutoBidsInitial);

    // Update itemEndTime for each item in auctionItems
    useEffect(() => {
      const updatedAuctionItems =
        auctionItems &&
        auctionItems.map((item: any) => {
          const bidMatch = auctionItemBidsLive.find(
            (bid: any) => bid.itemId === item.itemId
          );

          return {
            ...item,
            itemEndTime: bidMatch?.itemEndTime || item.itemEndTime,
          };
        });

      setAuctionItemsUpdated(updatedAuctionItems);
    }, [auctionItemBidsLive, auctionItems]);

    return (
      <AuctionItemListContext.Provider
        value={{
          auctionItems: auctionItemsUpdated,
          auctionItemBids,
          options,
          isLoading,
          setOptions,
          fetchBids,
          totalItems,
          serverTime,
          isFetching,
          error,
          maxItems,
          animateDuration,
          firstVisibleId,
          clearFilter,
          showResetFilterOnNotFound,
          visibleItemsData,
        }}
      >
        {!hidePagination && !error && (
          <AuctionBarActionsPanel
            showFilter={showFilter}
            filterProps={filterProps}
            searchValue={searchValue}
            layoutType={layoutType}
            setLayoutType={setLayoutType}
          />
        )}
        {paginationArgs && totalPaginationItems && !hidePaginationTop && (
          <div
            className={classNames(
              "flex flex-row items-center mx-auto justify-center mb-5 md:my-5"
            )}
          >
            {!searchLayout ? (
              <AuctionListItemPagination
                filterProps={filterProps}
                auctionText="Visa rop"
              />
            ) : (
              <AuctionPagination
                filterProps={filterProps}
                auctionText="Visa rop"
              />
            )}

            {setSearchValue && (
              <span className="ml-6 md:block">
                <Input
                  type="number"
                  placeholder="Sök Rop-nr"
                  value={searchValue}
                  icon="search"
                  buttonSize="md"
                  className="md:!text-base !text-base border border-gray-200"
                  inputClassName="md:!text-base !text-base max-w-[150px] !leading-[1] focus:!shadow-md"
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </span>
            )}
          </div>
        )}
        {(isLoading || !serverTime) && (
          <div className="self-stretch overflow-x-scroll md:overflow-hidden flex flex-col items-stretch gap-[10px] md:gap-[32px] text-sm xs-full-width pl-6 md:pl-0 my-8">
            {Array.from({ length: 3 }).map((_, i) => (
              <div className="flex flex-row" key={i}>
                <Skeleton height={352} containerClassName="w-[30%]" />
                <Skeleton height={352} containerClassName="flex-1" />
              </div>
            ))}
          </div>
        )}
        {layoutType === "grid" && <GridView />}
        {layoutType === "list" && <ListView />}
        {!isLoading && readMoreLink && maxItems < totalItems ? (
          <div className="flex justify-center w-full">{readMoreLink}</div>
        ) : null}
        {paginationArgs && totalPaginationItems ? (
          <div className="flex flex-row items-center mx-auto my-4 justify-center my-5">
            {!searchLayout ? (
              <AuctionListItemPagination
                filterProps={filterProps}
                auctionText="Visa rop"
              />
            ) : (
              <AuctionPagination
                filterProps={filterProps}
                auctionText="Visa rop"
              />
            )}
            {setSearchValue && (
              <span className="ml-6 hidden md:block">
                <Input
                  type="number"
                  placeholder="Sök Rop-nr"
                  value={searchValue}
                  icon="search"
                  buttonSize="md"
                  className="md:!text-base !text-base border border-gray-200"
                  inputClassName="md:!text-base !text-base max-w-[150px] !leading-[1] focus:!shadow-md"
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </span>
            )}
          </div>
        ) : (
          <div className="h-4" />
        )}
        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 bg-blue hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition-all duration-300 z-50"
            aria-label="Scroll to top"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 15l-6-6-6 6" />
            </svg>
          </button>
        )}
      </AuctionItemListContext.Provider>
    );
  }
);

AuctionListItems.displayName = "AuctionListItems";

export default AuctionListItems;
